import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { auditFetch } from '../utils/auditFetch';
import '../styles/Dashboard.css';

const emptyMetrics = {
  summary: {
    todayConsultations: 0,
    attendedPatients: 0,
    pendingAppointments: 0,
    activeDoctors: 0,
    averageWaitingMinutes: 0,
    cancellations: 0,
    noShows: 0,
    averageConsultationMinutes: 0,
  },
  specialties: [],
  doctors: [],
  statusBreakdown: [],
  recentConsultations: [],
  todayAppointments: [],
  patientBase: { active: 0, total: 0 },
};

const emptyClinicalIndicators = {
  summary: {
    medicalRecords: 0,
    uniqueDiagnoses: 0,
    chronicPatients: 0,
    referralsGenerated: 0,
    pendingFollowUps: 0,
    pendingReferences: 0,
  },
  frequentDiagnoses: [],
  ageGroups: [],
  chronicPatients: [],
  commonReasons: [],
  referrals: { total: 0, pending: 0, bySpecialty: [], recent: [] },
  pendingFollowUps: [],
};

const statusLabels = {
  pendiente: 'Pendiente',
  confirmada: 'Confirmada',
  sala_espera: 'Sala de espera',
  en_atencion: 'En atencion',
  atendida: 'Atendida',
  cancelada: 'Cancelada',
  reprogramada: 'Reprogramada',
  no_asistio: 'No asistio',
};

const AdminTelemedicineDashboard = ({ user }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [metrics, setMetrics] = useState(emptyMetrics);
  const [clinicalIndicators, setClinicalIndicators] = useState(emptyClinicalIndicators);
  const [statusMessage, setStatusMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);

  const auditActor = useMemo(() => ({
    id: user.id,
    role: user.role,
    name: user.name,
  }), [user.id, user.role, user.name]);

  const loadMetrics = useCallback(() => {
    setIsLoading(true);
    setStatusMessage('');
    if (user.demoMode) {
      auditFetch(`${backendUrl}/api/demo/presentation`, {}, auditActor)
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error('No se pudo cargar presentacion'))))
        .then((data) => {
          setMetrics(data.dashboard || emptyMetrics);
          setClinicalIndicators(data.clinicalIndicators || emptyClinicalIndicators);
        })
        .catch((error) => {
          console.error('Error loading demo dashboard:', error);
          setStatusMessage('No se pudieron cargar los indicadores de presentacion.');
        })
        .finally(() => setIsLoading(false));
      return;
    }

    Promise.all([
      auditFetch(`${backendUrl}/api/admin/dashboard`, {}, auditActor)
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error('No se pudo cargar el dashboard')))),
      auditFetch(`${backendUrl}/api/admin/clinical-indicators`, {}, auditActor)
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error('No se pudieron cargar indicadores clinicos')))),
    ])
      .then(([dashboardData, clinicalData]) => {
        setMetrics(dashboardData.metrics || emptyMetrics);
        setClinicalIndicators(clinicalData.indicators || emptyClinicalIndicators);
      })
      .catch((error) => {
        console.error('Error loading admin dashboard:', error);
        setStatusMessage('No se pudieron cargar los indicadores administrativos o clinicos.');
      })
      .finally(() => setIsLoading(false));
  }, [auditActor, backendUrl, user.demoMode]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const seedDashboard = async () => {
    if (user.demoMode) {
      setStatusMessage('Modo presentacion activo: los datos ficticios se cargan sin escribir en la base real.');
      loadMetrics();
      return;
    }
    setIsSeeding(true);
    setStatusMessage('');
    try {
      const response = await auditFetch(`${backendUrl}/api/admin/dashboard/seed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      }, auditActor);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo cargar presentacion');
      setStatusMessage(
        `Datos de presentacion cargados: ${data.result.appointments} citas, ${data.result.consultations} consultas, `
        + `${data.result.medicalRecords} registros clinicos y ${data.result.referrals} referencias.`
      );
      loadMetrics();
    } catch (error) {
      console.error('Error seeding admin dashboard:', error);
      setStatusMessage(error.message || 'No se pudo cargar la presentacion.');
    } finally {
      setIsSeeding(false);
    }
  };

  const summary = metrics.summary || emptyMetrics.summary;
  const clinicalSummary = clinicalIndicators.summary || emptyClinicalIndicators.summary;
  const maxSpecialtyCount = Math.max(1, ...(metrics.specialties || []).map((item) => item.count));
  const maxDiagnosisCount = Math.max(1, ...(clinicalIndicators.frequentDiagnoses || []).map((item) => item.count));

  return (
    <div className="admin-telemedicine-dashboard">
      <div className="section-header">
        <div>
          <h2>Dashboard administrativo de telemedicina</h2>
          <p>
            Consultas, agenda, medicos, tiempos y demanda por especialidad.
            {user.demoMode ? ' Modo presentacion activo con datos ficticios.' : ''}
          </p>
        </div>
        <div className="admin-dashboard-actions">
          <button className="btn btn-secondary" onClick={loadMetrics} disabled={isLoading}>
            {isLoading ? 'Actualizando...' : 'Actualizar'}
          </button>
          <button className="btn btn-primary" onClick={seedDashboard} disabled={isSeeding}>
            {user.demoMode ? 'Recargar presentacion' : (isSeeding ? 'Cargando presentacion...' : 'Cargar presentacion')}
          </button>
        </div>
      </div>

      {statusMessage && <p className="clinical-files-message">{statusMessage}</p>}

      <div className="admin-kpi-grid">
        <div className="admin-kpi-card">
          <span>Consultas del dia</span>
          <strong>{summary.todayConsultations}</strong>
          <small>Promedio {summary.averageConsultationMinutes} min</small>
        </div>
        <div className="admin-kpi-card">
          <span>Pacientes atendidos</span>
          <strong>{summary.attendedPatients}</strong>
          <small>{metrics.patientBase?.active || 0} activos en base</small>
        </div>
        <div className="admin-kpi-card">
          <span>Citas pendientes</span>
          <strong>{summary.pendingAppointments}</strong>
          <small>Incluye confirmadas y sala de espera</small>
        </div>
        <div className="admin-kpi-card">
          <span>Medicos activos</span>
          <strong>{summary.activeDoctors}</strong>
          <small>Con agenda vigente hoy</small>
        </div>
        <div className="admin-kpi-card">
          <span>Tiempo espera</span>
          <strong>{summary.averageWaitingMinutes} min</strong>
          <small>Promedio antes de atencion</small>
        </div>
        <div className="admin-kpi-card danger">
          <span>Cancelaciones</span>
          <strong>{summary.cancellations}</strong>
          <small>No asistencias: {summary.noShows}</small>
        </div>
      </div>

      <div className="admin-clinical-section">
        <div className="section-header">
          <div>
            <h2>Indicadores clinicos</h2>
            <p>Diagnosticos, motivos, edad, cronicos, referencias y seguimiento pendiente.</p>
          </div>
          <span className="admin-range-label">
            {clinicalIndicators.range?.from || '30 dias'} - {clinicalIndicators.range?.to || 'hoy'}
          </span>
        </div>

        <div className="admin-kpi-grid clinical">
          <div className="admin-kpi-card">
            <span>Registros clinicos</span>
            <strong>{clinicalSummary.medicalRecords}</strong>
            <small>{clinicalSummary.uniqueDiagnoses} diagnosticos unicos</small>
          </div>
          <div className="admin-kpi-card">
            <span>Pacientes cronicos</span>
            <strong>{clinicalSummary.chronicPatients}</strong>
            <small>Segun antecedentes registrados</small>
          </div>
          <div className="admin-kpi-card">
            <span>Referencias</span>
            <strong>{clinicalSummary.referralsGenerated}</strong>
            <small>{clinicalSummary.pendingReferences} pendiente(s)</small>
          </div>
          <div className="admin-kpi-card">
            <span>Seguimiento pendiente</span>
            <strong>{clinicalSummary.pendingFollowUps}</strong>
            <small>Controles solicitados en HCU</small>
          </div>
        </div>

        <div className="admin-dashboard-grid">
          <div className="admin-panel">
            <h3>Diagnosticos frecuentes</h3>
            {(clinicalIndicators.frequentDiagnoses || []).length === 0 ? (
              <p className="audit-empty">Sin diagnosticos registrados.</p>
            ) : clinicalIndicators.frequentDiagnoses.map((diagnosis) => (
              <div key={diagnosis.name} className="admin-bar-row">
                <div>
                  <strong>{diagnosis.name}</strong>
                  <span>{diagnosis.count} caso(s)</span>
                </div>
                <div className="admin-bar-track">
                  <span style={{ width: `${Math.max(10, (diagnosis.count / maxDiagnosisCount) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>

          <div className="admin-panel">
            <h3>Grupos de edad atendidos</h3>
            {(clinicalIndicators.ageGroups || []).map((group) => (
              <div key={group.name} className="admin-status-row">
                <span>{group.name}</span>
                <strong>{group.count}</strong>
              </div>
            ))}
          </div>

          <div className="admin-panel">
            <h3>Motivos de consulta</h3>
            {(clinicalIndicators.commonReasons || []).length === 0 ? (
              <p className="audit-empty">Sin motivos registrados.</p>
            ) : clinicalIndicators.commonReasons.map((reason) => (
              <div key={reason.name} className="admin-status-row">
                <span>{reason.name}</span>
                <strong>{reason.count}</strong>
              </div>
            ))}
          </div>

          <div className="admin-panel">
            <h3>Referencias por especialidad</h3>
            {(clinicalIndicators.referrals?.bySpecialty || []).length === 0 ? (
              <p className="audit-empty">Sin referencias generadas.</p>
            ) : clinicalIndicators.referrals.bySpecialty.map((specialty) => (
              <div key={specialty.name} className="admin-status-row">
                <span>{specialty.name}</span>
                <strong>{specialty.count}</strong>
              </div>
            ))}
          </div>

          <div className="admin-panel admin-panel-wide">
            <h3>Pacientes cronicos</h3>
            <div className="admin-table compact">
              {(clinicalIndicators.chronicPatients || []).length === 0 ? (
                <p className="audit-empty">Sin pacientes cronicos identificados.</p>
              ) : clinicalIndicators.chronicPatients.map((patient) => (
                <div key={patient.id} className="admin-table-row clinical">
                  <strong>{patient.name}</strong>
                  <span>{patient.age ? `${patient.age} años` : 'Edad no registrada'}</span>
                  <span>{patient.city || 'Ciudad no registrada'}</span>
                  <em>{patient.medicalHistory || 'Antecedente no especificado'}</em>
                </div>
              ))}
            </div>
          </div>

          <div className="admin-panel admin-panel-wide">
            <h3>Seguimiento pendiente</h3>
            <div className="admin-table compact">
              {(clinicalIndicators.pendingFollowUps || []).length === 0 ? (
                <p className="audit-empty">Sin seguimientos pendientes.</p>
              ) : clinicalIndicators.pendingFollowUps.map((followUp) => (
                <div key={followUp.id} className="admin-table-row clinical">
                  <strong>{followUp.patientName}</strong>
                  <span>{followUp.date || 'Fecha pendiente'} {followUp.time || ''}</span>
                  <span>{followUp.mode}</span>
                  <em>{followUp.reason}</em>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="admin-dashboard-grid">
        <div className="admin-panel">
          <h3>Especialidades mas usadas</h3>
          {(metrics.specialties || []).length === 0 ? (
            <p className="audit-empty">Sin citas para calcular demanda.</p>
          ) : metrics.specialties.map((specialty) => (
            <div key={specialty.name} className="admin-bar-row">
              <div>
                <strong>{specialty.name}</strong>
                <span>{specialty.count} cita(s)</span>
              </div>
              <div className="admin-bar-track">
                <span style={{ width: `${Math.max(10, (specialty.count / maxSpecialtyCount) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>

        <div className="admin-panel">
          <h3>Estado de agenda</h3>
          {(metrics.statusBreakdown || []).map((item) => (
            <div key={item.name} className="admin-status-row">
              <span>{statusLabels[item.name] || item.name}</span>
              <strong>{item.count}</strong>
            </div>
          ))}
        </div>

        <div className="admin-panel admin-panel-wide">
          <h3>Citas de hoy</h3>
          <div className="admin-table">
            {(metrics.todayAppointments || []).length === 0 ? (
              <p className="audit-empty">No hay citas registradas para hoy.</p>
            ) : metrics.todayAppointments.map((appointment) => (
              <div key={appointment.id} className="admin-table-row">
                <span>{new Date(appointment.datetime).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}</span>
                <strong>{appointment.patientName}</strong>
                <span>{appointment.doctorName}</span>
                <span>{appointment.specialty}</span>
                <em>{statusLabels[appointment.status] || appointment.status}</em>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-panel admin-panel-wide">
          <h3>Consultas recientes</h3>
          <div className="admin-table">
            {(metrics.recentConsultations || []).length === 0 ? (
              <p className="audit-empty">No hay consultas cerradas hoy.</p>
            ) : metrics.recentConsultations.map((consultation) => (
              <div key={consultation.id} className="admin-table-row">
                <span>{consultation.endedAt ? new Date(consultation.endedAt).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }) : '--:--'}</span>
                <strong>{consultation.patientName}</strong>
                <span>{consultation.specialty}</span>
                <span>{consultation.durationMinutes} min</span>
                <em>{consultation.doctorId}</em>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTelemedicineDashboard;
